import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { byName } from '@pubstack/common/src/sort'

/** How many stacks use a rule, across both products — `usedInStacks` is keyed by site. */
export const stackCountOf = (rule: BidderRule): number => Object.values(rule.usedInStacks ?? {}).reduce((total, stacks) => total + stacks.length, 0)

type SortOrder = 'ascending' | 'descending' | 'none'

/**
 * A Pubstack default rule ahead of the others, whichever way the column is sorted; `0` when both or neither are.
 *
 * `handleTableSort` multiplies a comparator's result by the direction, so the answer is multiplied by it here
 * first and the two cancel out. Each side is asked on its own, so the comparator stays consistent: it never
 * says both a-before-b and b-before-a for one pair.
 */
const defaultsFirst = (a: BidderRule, b: BidderRule, order: SortOrder): number => {
  if (!!a.isDefault === !!b.isDefault) return 0
  // The same sign `handleTableSort` applies, `none` included.
  const direction = order === 'ascending' ? 1 : -1
  return (a.isDefault ? -1 : 1) * direction
}

/** The two sortable columns of the bidder rules list: the defaults on top, then the column's own order. */
export const byRuleName = (a: BidderRule, b: BidderRule, order: SortOrder): number => defaultsFirst(a, b, order) || byName(a, b)

export const byStackCount = (a: BidderRule, b: BidderRule, order: SortOrder): number => defaultsFirst(a, b, order) || stackCountOf(a) - stackCountOf(b)
